import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, MessageCircle, Clock } from "lucide-react";
import { Seo, breadcrumbLd } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { StepForm } from "@/components/site/StepForm";
import { BookCallButton } from "@/components/site/Ctas";
import { useSettings, FALLBACK_EMAIL } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/data/site";

const MODES = {
  audit: { lines: ["Get your free", "digital audit."], label: "Request My Free Audit", services: [] },
  call: { lines: ["Book a", "discovery call."], label: "Request a Discovery Call", services: [] },
  quote: { lines: ["Request a", "custom quote."], label: "Request My Quote", services: [] },
  contact: { lines: ["Let's talk about", "your marketing."], label: "Send Enquiry", services: [] },
};

export default function Contact() {
  const [params] = useSearchParams();
  const s = useSettings();
  const type = MODES[params.get("type")] ? params.get("type") : "contact";
  const mode = MODES[type];
  const svcParam = params.get("service");
  const services = svcParam && SERVICE_OPTIONS.includes(svcParam) ? [svcParam] : mode.services;
  const email = s.contact_email || FALLBACK_EMAIL;
  const crumbs = [["Home", "/"], ["Contact", "/contact"]];

  return (
    <>
      <Seo title="Contact Freya | Free Digital Marketing Audit" path="/contact"
        description="Contact Digital AdDoctor in Tunbridge Wells, Kent. Request a free digital marketing audit, book a discovery call or ask for a custom quote." jsonLd={[breadcrumbLd(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow="Contact" lines={mode.lines}
        intro="Tell me a little about your business and what you'd like to improve. I read every enquiry personally and reply within one to two working days." />
      <section className="bg-[#F5F0FF] py-20 sm:py-24" data-testid="contact-page">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1.5fr_1fr]">
          <Reveal><StepForm key={type + svcParam} formType={type} submitLabel={mode.label} defaultServices={services} /></Reveal>
          <div className="space-y-4">
            <Reveal className="rounded-[28px] bg-[#13052A] p-7 text-white">
              <p className="eyebrow text-violet-300">Prefer to talk?</p>
              <p className="mt-3 text-lg font-bold">Message me on WhatsApp for a quick chat.</p>
              {s.whatsapp_number ? (
                <a href={`https://wa.me/${s.whatsapp_number.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-extrabold text-[#13052A] transition-transform hover:-translate-y-0.5">
                  <MessageCircle className="h-4 w-4" /> Call Me on WhatsApp
                </a>
              ) : (
                <BookCallButton className="btn-primary mt-5 w-full" testid="contact-book-call" />
              )}
            </Reveal>
            <Reveal delay={0.05} className="space-y-4 rounded-[28px] bg-white p-7">
              <a href={`mailto:${email}`} className="flex items-center gap-3 text-sm font-bold text-[#13052A] hover:text-[#7C3AED]" data-testid="contact-email-link"><Mail className="h-5 w-5 text-[#7C3AED]" /> {email}</a>
              {s.phone && <a href={`tel:${s.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-sm font-bold text-[#13052A]" data-testid="contact-phone-link">{s.phone}</a>}
              {s.whatsapp_number && (
                <a href={`https://wa.me/${s.whatsapp_number.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm font-bold text-[#13052A]" data-testid="contact-whatsapp-link">
                  <MessageCircle className="h-5 w-5 text-[#25D366]" /> Message on WhatsApp
                </a>
              )}
              <p className="flex items-center gap-3 text-sm text-slate-600"><MapPin className="h-5 w-5 text-[#7C3AED]" /> {s.location || "Tunbridge Wells, Kent, United Kingdom"}</p>
              <p className="flex items-center gap-3 text-sm text-slate-600"><Clock className="h-5 w-5 text-[#7C3AED]" /> Serving Kent, Sussex, Surrey, London & UK-wide</p>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
